import {
  PagedResponseEmpty,
  ValidationError,
} from "@ofqual/portal-core/core/types";
import { combineReducers, AnyAction } from "redux";
import { Attachment } from "../domain/Attachment";
import {
  INPUTTING_ITEM,
  CREATED_ITEM,
  CREATING_ITEM,
  CREATING_ITEM_VALIDATION_FAIL,
  LOADED_ITEM,
  LOADED_ITEMS,
  LOADING_ITEM,
  LOADING_ITEMS,
  SAVED_ITEM,
  UPDATING_ITEM,
  DELETE_ATTACHMENT,
  ASSIGN_FOI,
  CLOSE_FOI,
  REOPEN_FOI,
  CANCEL_FOI,
  UNASSIGN_FOI,
  LOADED_SHARE,
  UPDATE_STAGE,
} from "./actions";
import { CreateItemState, ItemsState, ItemState } from "./state";
import { failedCrudResult, noCrudResult, successCrudResult } from "./types";

const itemsInitialState: ItemsState = {
  status: "NOT_LOADED",
  response: PagedResponseEmpty,
  hasData: false,
};

const items = (
  state: ItemsState = itemsInitialState,
  action: AnyAction
): ItemsState => {
  const { type, payload } = action;
  if (type === LOADING_ITEMS) {
    return {
      ...state,
      status: "LOADING",
    };
  }

  if (type === LOADED_ITEMS) {
    return {
      ...state,
      status: "LOADED",
      response: payload,
      hasData: true,
    };
  }
  return state;
};

const itemInitialState: ItemState = {
  status: "NOT_LOADED",
  item: null,
  share: null,
  lastCrudResult: noCrudResult(),
  attachmentsToDelete: [],
};

const item = (
  state: ItemState = itemInitialState,
  action: AnyAction
): ItemState => {
  const { type, payload } = action;
  switch (type) {
    case LOADING_ITEM:
      return {
        ...state,
        item: null,
        status: "LOADING",
        attachmentsToDelete:[],
        lastCrudResult: noCrudResult(),
      };
    case UPDATING_ITEM:
    case ASSIGN_FOI:
    case CLOSE_FOI:
    case REOPEN_FOI:
    case CANCEL_FOI:
    case UPDATE_STAGE:
    case UNASSIGN_FOI:
      return {
        ...state,
        status: "UPDATING",
        lastCrudResult: noCrudResult(),
      };
    case LOADED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload.item,
        share: payload.share ?? null,
      };
    case SAVED_ITEM:
      return {
        ...state,
        status: "LOADED",
        item: payload,
        lastCrudResult: successCrudResult(),
      };
    case DELETE_ATTACHMENT:
      const { attachment } = payload as {
        attachment: Attachment;
      };
      return {
        ...state,
        item: {
          ...state.item!,
          supportingDocuments: state.item!.supportingDocuments?.filter(
            (x) => x.id !== attachment.id
          ),
        },
        attachmentsToDelete: [...state.attachmentsToDelete, attachment],
      };
    case LOADED_SHARE:
      return {
        ...state,
        share: payload,
      };
    default:
      return state;
  }
};

const createItemInitialState: CreateItemState = {
  status: "INPUTTING",
  lastCrudResult: noCrudResult(),
};
const createItem = (
  state: CreateItemState = createItemInitialState,
  action: AnyAction
): CreateItemState => {
  const { type, payload } = action;
  if (type === CREATING_ITEM) {
    return {
      ...state,
      status: "CREATING",
    };
  }
  if (type === CREATING_ITEM_VALIDATION_FAIL) {
    return {
      ...state,
      lastCrudResult: failedCrudResult(payload as ValidationError),
      status: "INPUTTING",
    };
  }
  if (type === CREATED_ITEM) {
    return {
      ...state,
      lastCrudResult: noCrudResult(),
      status: "CREATED",
    };
  }
  if (type === INPUTTING_ITEM) {
    return {
      ...state,
      lastCrudResult: noCrudResult(),
      status: "INPUTTING",
    };
  }
  return state;
};

export const rootReducer = combineReducers({
  items,
  item,
  createItem,
});
